import React, { useEffect, useState } from "react"
import { navigate } from "vike/client/router"
import { useSearchParamValue } from "aldoo-ra/SearchParams"
import PlatformAuth from "aldoo-ra/PlatformAuth"
import CMSData from "aldoo-ra/CMS/cms-data"
import Button from "aldoo-ra/Button"
import Spinner from "aldoo-ra/Spinner"
import Modal from "aldoo-ra/Modal"
import Typography from "aldoo-ra/Typography"
import PubSub from "pubsub-js"
import Navbar from "@components/Navbar"

import ProfileSection from "./sections/ProfileSection"
import OrganizationsSection from "./sections/OrganizationsSection"

const DEFAULT_MENU = [
  { label: "Profile",       options: { action: "profile" } },
  { label: "Organizations", options: { action: "organizations" } },
  { label: "Logout",        options: { action: "logout" } },
]

function getSectionId(item) {
  const a = item?.options?.action
  return a && a !== "logout" ? a : null
}

const Account = ({ pageContext }) => {
  const [auth] = PlatformAuth.useAuth()
  const logout = PlatformAuth.useLogout()
  const sectionParam = useSearchParamValue("section")

  // Allow path-style /account/projects too
  const pathSection = (() => {
    const p = pageContext?.urlPathname || ""
    const m = p.match(/^\/account\/([a-z0-9-]+)/i)
    return m ? m[1] : null
  })()

  const { data: menuData } = CMSData({
    contentType: "MenuItem",
    match: { "content.name": "AccountSitePanel" },
    itemsPerPage: 1,
  })

  const menuItems = (menuData?.[0]?.content?.items || menuData?.[0]?.items || DEFAULT_MENU)
  const navItems = menuItems.filter((i) => i.options?.action !== "logout")
  const actionItems = menuItems.filter((i) => i.options?.action === "logout")

  const defaultSection = getSectionId(navItems[0]) || "profile"
  const valid = new Set(navItems.map(getSectionId).filter(Boolean))
  const activeSection = valid.has(pathSection)
    ? pathSection
    : valid.has(sectionParam)
      ? sectionParam
      : defaultSection

  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false)

  useEffect(() => {
    if (auth === null) {
      PubSub.publish("signin", { redirect: "/account" })
    }
  }, [auth])

  if (auth === undefined) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Spinner className="w-6 h-6" />
      </div>
    )
  }
  if (!auth) return null

  const goSection = (id) => navigate(`/account?section=${id}`)
  const handleAction = (item) => {
    if (item.options?.action === "logout") setShowLogoutConfirm(true)
  }
  const onLogout = async () => {
    setShowLogoutConfirm(false)
    await logout()
    navigate("/")
  }

  let SectionEl = null
  if (activeSection === "profile") SectionEl = <ProfileSection profile={auth} />
  else if (activeSection === "organizations") SectionEl = <OrganizationsSection />
  else SectionEl = <div className="text-admin_muted">Unknown section: {activeSection}</div>

  return (
    <div className="min-h-screen bg-paper">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 pt-24 pb-16">
        <div className="flex flex-col md:flex-row gap-6 md:items-start">
          {/* Sidebar */}
          <nav className="md:w-56 shrink-0 bg-paper rounded-xl border border-bordered p-2 flex md:flex-col gap-0.5 overflow-x-auto">
            {navItems.map((item) => {
              const id = getSectionId(item)
              const active = id === activeSection
              return (
                <button
                  key={id}
                  onClick={() => goSection(id)}
                  className={`flex items-center gap-3 w-full text-left px-4 py-2.5 rounded-xl font-medium text-sm whitespace-nowrap transition-colors
                    ${active
                      ? "bg-primary text-white"
                      : "text-text_secondary hover:text-text hover:bg-black/5"}`}
                >
                  {item.label}
                </button>
              )
            })}
            {actionItems.length > 0 && (
              <div className="my-1.5 border-t border-bordered mx-2" />
            )}
            {actionItems.map((item) => (
              <button
                key={item.options.action}
                onClick={() => handleAction(item)}
                className="flex items-center gap-3 w-full text-left px-4 py-2.5 rounded-xl font-medium text-sm transition-colors text-error hover:bg-black/5"
              >
                {item.label}
              </button>
            ))}
          </nav>

          {/* Section content */}
          <div className="flex-1 min-w-0">
            <Typography variant="admin_h1" className="mb-4">{activeSection}</Typography>
            {SectionEl}
          </div>
        </div>
      </div>

      {showLogoutConfirm && (
        <Modal onClose={() => setShowLogoutConfirm(false)}>
          <div className="p-6 space-y-4">
            <Typography variant="admin_h3">Log out?</Typography>
            <div className="flex gap-2 justify-end">
              <Button variant="secondary" onClick={() => setShowLogoutConfirm(false)}>Cancel</Button>
              <Button variant="danger" onClick={onLogout}>Log out</Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

export default Account
